import * as React from 'react';
import classNames from 'classnames';
import { ApolloQueryResult } from '@apollo/client/core/types';
//import { ViewerProfile } from '../../../../../../common/types/socket-types';
import { AllowedSidebarMap } from '../../../hooks';
import {
  AnalyticsEventSource,
  ScreenEventAnalytics,
  UNKNOWN_SCREEN,
  ScreenType,
  PAGETITLE,
} from '@atlassian/temp-analytics';
import { AppContent, AppPage, PageHeaderV2, SummarySectionV2 } from '../../../application';
import { ErrorPanel } from '../../../error-page';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
//import CommerceNoteSearch from '../../../common/containers/commerce-note-search';
import {
  getDefaultActions,
  getDefaultBreadcrumbTrail,
  getDefaultHeader,
  getDefaultIcon,
  getDefaultSubheader,
} from '../billing-doc-utils';
import BillingDocsBreadcrumbs from './billing-doc-breadcrumbs';
import BillingDocDetails from './billing-doc-details';
//import BillingDocMessage from './billing-doc-message';
import './billing-doc-page.scss';
import { SourceSystem } from '@atlassian/cssxp-typescommon';
import { BillingDocWithLineItems, Invoice, BaseRoutes } from '@atlassian/cssxp-types-billing';
import { useFeatureToggle } from '../../../hooks/src/use-feature-toggle';
import { AuditNotesV2, AuditNotesV2Props } from '../../../audit-notes/src/audit-notes-v2';
import { PaymentRetryV2 } from '../../../payment-retry/index';
import { ENABLE_INVOICE_VIEW_PAYMENT_RETRY } from '@atlassian/cssxp-utils-generic';

interface Props {
  analyticsScreenSource?: AnalyticsEventSource;
  additionalDetailsContent?: React.ReactNode;
  getBreadcrumbTrail?: typeof getDefaultBreadcrumbTrail;
  headerProps?: {
    getHeader?: typeof getDefaultHeader;
    getSubheader?: typeof getDefaultSubheader;
    getIcon?: typeof getDefaultIcon;
    getActions?: typeof getDefaultActions;
    viewers?: [];
  };
  billingDoc?: BillingDocWithLineItems;
  isFetchingBillingDoc: boolean;
  billingDocFetchError: Error | null;
  auditTrailProps?: AuditNotesV2Props;
  BillingDocLineItems?: React.ComponentType<
    BillingDocWithLineItems & BaseRoutes & { shouldDisableRefundsSidebar?: boolean }
  >;
  allowedSidebars?: AllowedSidebarMap;
  paymentRetry?: Invoice['dunningStatus'];
  approvalSection?: React.ReactNode;
  onComplete: () => Promise<ApolloQueryResult<unknown>>;
  appPageClass?: string;
  /* The `entitlementBaseRoute` prop is used in apps like PPC to correctly redirect
   * to its entitlement page which has different route structure than AC.
   * Eg.: /any-custom-route-for-entitlement/
   * */
  entitlementBaseRoute?: string;
  /* The `refundsBaseRoute` prop is used in apps like PPC to correctly redirect
   * to its refunds details page which has different route structure than AC.
   * Eg.: /any-custom-route-for-refunds/
   * */
  refundsBaseRoute?: string;
  shouldDisableRefundsSidebar?: boolean;
}
/**
 * Common Billing Document Page for Invoices, Quotes, Refunds
 */
export const BillingDocPageV2 = ({
  billingDoc,
  isFetchingBillingDoc,
  billingDocFetchError,
  headerProps,
  auditTrailProps,
  analyticsScreenSource,
  additionalDetailsContent,
  BillingDocLineItems,
  getBreadcrumbTrail,
  allowedSidebars,
  paymentRetry,
  onComplete,
  approvalSection,
  appPageClass,
  entitlementBaseRoute,
  refundsBaseRoute,
  shouldDisableRefundsSidebar,
}: Props) => {
  //TODO: Need to find if we need this orgaization and if need being in another story
  /*
  const domain = billingDoc?.domain;
  React.useEffect(() => {
    if (domain) {
      dispatch(fetchBillingDocOrganization(domain));
    }
  }, [domain, dispatch]);*/
  const organization = undefined;

  const [isEnabled] = useFeatureToggle();

  if (isFetchingBillingDoc) {
    return <LoadingIndicator />;
  }

  if (billingDocFetchError) {
    return (
      <div data-testid="error-panel">
        <ErrorPanel error={billingDocFetchError} />
      </div>
    );
  }

  if (!billingDoc) {
    return null;
  }

  const renderHeader = () => {
    const getHeader = headerProps?.getHeader ?? getDefaultHeader;
    const getSubheader = headerProps?.getSubheader ?? getDefaultSubheader;
    const getIcon = headerProps?.getIcon ?? getDefaultIcon;
    const getActions = headerProps?.getActions ?? getDefaultActions;

    return (
      <PageHeaderV2
        icon={getIcon(billingDoc)}
        header={getHeader(billingDoc)}
        subheader={getSubheader(billingDoc)}
        actions={getActions(billingDoc, onComplete)}
        viewers={headerProps?.viewers}
      />
    );
  };

  const renderBreadcrumbs = () => {
    const breadcrumbTrail = (getBreadcrumbTrail ?? getDefaultBreadcrumbTrail)(billingDoc, organization);
    return <BillingDocsBreadcrumbs breadcrumbTrail={breadcrumbTrail} />;
  };

  // eslint-disable-next-line no-unused-vars
  const renderAuditTrail = (_sourceSystem?: SourceSystem | null) => {
    if (auditTrailProps) {
      return <AuditNotesV2 {...auditTrailProps} />;
    }
    return null;
  };

  const renderLineItems = (): JSX.Element | undefined => {
    if (BillingDocLineItems) {
      return (
        <BillingDocLineItems
          entitlementBaseRoute={entitlementBaseRoute}
          refundsBaseRoute={refundsBaseRoute}
          shouldDisableRefundsSidebar={shouldDisableRefundsSidebar}
          {...billingDoc}
        />
      );
    }
  };

  const renderApprovalSection = () => {
    return <div style={{ marginTop: 32 }}>{approvalSection}</div>;
  };

  return (
    <AppPage className={classNames('billing-document', appPageClass)} allowedSidebars={allowedSidebars}>
      <ScreenEventAnalytics
        event={{
          name: analyticsScreenSource ?? UNKNOWN_SCREEN,
          attributes: {
            id: billingDoc.id,
            entityName:
              analyticsScreenSource === AnalyticsEventSource.INVOICE_SCREEN
                ? PAGETITLE.INVOICE
                : analyticsScreenSource === AnalyticsEventSource.REFUND_SCREEN
                ? PAGETITLE.CREDIT_NOTES
                : null,
          },
        }}
      />
      {renderBreadcrumbs()}
      <AppContent>
        <>{renderHeader()}</>
        {/* TODO: Need to change further if need HAMS related invoice */}
        {/*{billingDoc.sourceSystem === SourceSystem.HAMS && (
          <>
             Perhaps not pass audit trail, maybe create an entity enum 
            <BillingDocMessage entity={'invoice'} adaUrl={auditTrailProps?.adaUrl} />
          </>
        )}*/}

        <SummarySectionV2.SummarySection
          testId="summary"
          className="summary-section-wrapper"
          columns="repeat(auto-fit, minmax(100px, 1fr))"
        >
          <BillingDocDetails additionalContent={additionalDetailsContent} billingDoc={billingDoc} />
        </SummarySectionV2.SummarySection>

        <div className="billing-docs-container">
          {renderLineItems()}
          {renderAuditTrail(billingDoc.sourceSystem)}
          {renderApprovalSection()}
          {paymentRetry && isEnabled(ENABLE_INVOICE_VIEW_PAYMENT_RETRY) && (
            <PaymentRetryV2 dunningInfo={paymentRetry} />
          )}
        </div>
      </AppContent>
    </AppPage>
  );
};

export default BillingDocPageV2;
